import type { ModuleFederationRuntimePlugin } from '@module-federation/enhanced/runtime'
import { satisfy } from '@module-federation/runtime-core'

// Why this exists: every remote shares @inno-ui/* (and most libraries) as
// `singleton: false` under `shareStrategy: 'version-first'`. That strategy
// only treats a copy as taken once it has finished loading. A remote that
// resolves the same package while another remote's copy is still *loading*
// picks the newest registered version and loads a second copy.
//
// A cold first load rarely hits that window, because remote entries arrive
// one at a time. A warm-cache reload, or opening a second app, initialises
// remotes together, so copies that fit the same range (date-picker 1.35.7 and
// 1.36.1, button 1.0.0 and 1.0.2) start loading side by side.
//
// Remotes resolve shares inside their own federation instance, so host
// `shared`, `shareStrategy` and host plugins never reach them. A global plugin
// does, if it is registered before the first remoteEntry runs. `beforeLoadShare`
// is the one hook that carries the consumer's range in every runtime the fleet
// ships (0.15 to 2.x): the plugin picks the version itself and hands Module
// Federation that exact version as the requirement, so every runtime's own
// resolver lands on it.

type ShareEntry = { loaded?: boolean; loading?: unknown; lib?: unknown }

export const shareReuseStats = {
  calls: 0,
  reused: 0,
  highest: 0,
  skipped: 0,
  waits: 0,
  waitedMs: 0,
  maxWaitMs: 0,
  timeouts: 0,
  heldAgainstReplace: 0
}

// Entries this plugin has already handed to a consumer. The runtime marks an
// entry `loading` only after `beforeLoadShare` resolves, a few microtasks
// later; a batch of consumes resolving together would otherwise each see
// "nothing in use" and pick different copies in that gap.
const claimed = new WeakSet<ShareEntry>()

// Entries whose download failed. Module Federation never clears a rejected
// `loading`, so without this every later consumer in range would be steered
// to (and held on) a copy that can never load.
const failed = new WeakSet<ShareEntry>()
const watched = new WeakSet<ShareEntry>()

function watchLoading(entry: ShareEntry) {
  const { loading } = entry
  if (watched.has(entry) || !(loading instanceof Promise)) return
  watched.add(entry)
  loading.catch(() => failed.add(entry))
}

const isInUse = (entry: ShareEntry) => {
  watchLoading(entry)
  if (failed.has(entry)) return false
  return Boolean(
    entry.loaded || entry.lib || entry.loading || claimed.has(entry)
  )
}

const highest = (versions: string[]) =>
  versions.reduce<string | undefined>(
    (best, version) =>
      best === undefined || satisfy(version, `>${best}`) ? version : best,
    undefined
  )

/**
 * The version a consumer with `range` should use: the highest copy that is
 * already loaded or loading and fits the range, otherwise the highest
 * registered version that fits. `undefined` when nothing fits, which leaves
 * the decision to Module Federation.
 */
export function pickSharedVersion(
  versions: Record<string, ShareEntry>,
  range: string | false | undefined
): string | undefined {
  const fits = Object.keys(versions).filter(
    version =>
      range === false ||
      range === undefined ||
      range === '*' ||
      satisfy(version, range)
  )
  const inUse = fits.filter(version => isInUse(versions[version]))
  return highest(inUse.length > 0 ? inUse : fits)
}

// Remotes the container has asked for whose entry has not initialised yet,
// i.e. whose shared versions are not registered. Starting a fresh copy before
// they register picks from an incomplete list: the first remote to boot can
// only choose its own (possibly oldest) copy, and a later remote whose range
// starts above it must load another. Waiting for them first lets the choice
// see every version on the page.
//
// ponytail: a fixed cap, not a measured one. A remote that never boots (failed
// entry, CDN fallback) is dropped after one cap so it cannot stall the page
// again; reuse of a copy already in use never waits. Raise the cap only with a
// cold-load measurement of time to first widget next to the copy count.
export const BOOT_WAIT_MS = 500
const booting = new Map<string, { done: Promise<void>; finish: () => void }>()
const booted = new Set<string>()
// Missed the cap once: a later registerRemote for it (re-render, slow entry)
// must not cost every fresh copy another wait. A *failed* remote is not given
// up on: the container re-registers it with its fallback URL, a real new load.
const givenUp = new Set<string>()

/** The container is about to load this remote's entry. */
export function remoteRequested(name: string) {
  if (booted.has(name) || booting.has(name) || givenUp.has(name)) return
  let finish = () => {}
  const done = new Promise<void>(resolve => {
    finish = resolve
  })
  booting.set(name, { done, finish })
}

function stopWaitingFor(name: string) {
  booting.get(name)?.finish()
  booting.delete(name)
}

/** The remote's entry failed to load: nothing more will register. */
export function remoteFailed(name: string) {
  stopWaitingFor(name)
}

function remoteBooted(name: string) {
  booted.add(name)
  stopWaitingFor(name)
}

async function waitForBootingRemotes() {
  const batch = [...booting.entries()]
  if (batch.length === 0) return
  const started = Date.now()
  let timer: ReturnType<typeof setTimeout> | undefined
  const timedOut = await Promise.race([
    Promise.all(batch.map(([, boot]) => boot.done)).then(() => false),
    new Promise<boolean>(resolve => {
      timer = setTimeout(() => resolve(true), BOOT_WAIT_MS)
    })
  ])
  clearTimeout(timer)
  if (timedOut) {
    shareReuseStats.timeouts += 1
    // Releases every consumer parked on these remotes, not only this one:
    // a remote that missed the cap once is not waited for again.
    for (const [name, boot] of batch)
      if (booting.get(name) === boot) {
        givenUp.add(name)
        stopWaitingFor(name)
      }
  }
  const waited = Date.now() - started
  shareReuseStats.waits += 1
  shareReuseStats.waitedMs += waited
  shareReuseStats.maxWaitMs = Math.max(shareReuseStats.maxWaitMs, waited)
}

type BeforeLoadShareArgs = Parameters<
  NonNullable<ModuleFederationRuntimePlugin['beforeLoadShare']>
>[0]

/** The registered versions of the package and the one to use, if any fits. */
function choose(args: BeforeLoadShareArgs) {
  const { pkgName, shareInfo, origin } = args
  const shareConfig = shareInfo?.shareConfig
  if (!shareInfo || !shareConfig) return undefined

  for (const scope of [shareInfo.scope ?? 'default'].flat()) {
    const versions = origin.shareScopeMap?.[scope]?.[pkgName]
    if (!versions) continue
    const version = pickSharedVersion(versions, shareConfig.requiredVersion)
    // A singleton picks the highest registered version not yet *loaded*, so a
    // copy another remote is still loading loses to a newer registration and
    // a second copy starts. Steer it onto a copy in use that fits its range;
    // with none, the runtime's own singleton pick stands.
    if (
      shareConfig.singleton &&
      (version === undefined || !isInUse(versions[version]))
    )
      return undefined
    return { versions, version }
  }
  return undefined
}

/** A consumer is about to start a copy while another version already runs. */
export type SecondCopy = {
  pkgName: string
  /** The copy being started; undefined when no registered version fits. */
  version: string | undefined
  range: string | false | undefined
  inUse: string[]
  remote: string
}

// One report per package@version per page: a second copy is a fact about the
// deployed bundles, not about each consumer that hits it.
const reported = new Set<string>()

function reportSecondCopy(
  args: BeforeLoadShareArgs,
  { versions, version }: NonNullable<ReturnType<typeof choose>>,
  onSecondCopy: ((copy: SecondCopy) => void) | undefined
) {
  if (!onSecondCopy) return
  if (version !== undefined && isInUse(versions[version])) return
  const inUse = Object.keys(versions).filter(v => isInUse(versions[v]))
  if (inUse.length === 0) return
  const range = args.shareInfo?.shareConfig?.requiredVersion
  const key = `${args.pkgName}@${version ?? String(range)}`
  if (reported.has(key)) return
  reported.add(key)
  onSecondCopy({
    pkgName: args.pkgName,
    version,
    range,
    inUse,
    remote: args.origin?.name
  })
}

// Module Federation's `register` replaces a registered version that is not
// `loaded` yet whenever the newcomer's name sorts higher (`hostName > from`),
// also while that version is loading. Consumers resolving after the swap then
// download the identical version a second time from the newcomer. Once a
// consumer has been handed an entry, keep that entry in the scope: a later
// registration of the same version becomes a no-op. Done with an accessor so
// it holds for every runtime version's `register`, not one implementation.
// Two replacements still go through: one for an entry whose download failed,
// and an eager newcomer that already carries its module (`lib`) for a held
// entry that does not, because `loadShareSync` can only use a `lib`.
function holdInScope(
  versions: Record<string, ShareEntry>,
  version: string,
  entry = versions[version]
) {
  if (Object.getOwnPropertyDescriptor(versions, version)?.get) return
  if (!entry || versions[version] !== entry) return
  Object.defineProperty(versions, version, {
    configurable: true,
    enumerable: true,
    get: () => entry,
    set: (next: ShareEntry | undefined) => {
      if (failed.has(entry) || (next?.lib && !entry.lib)) {
        Object.defineProperty(versions, version, {
          configurable: true,
          enumerable: true,
          writable: true,
          value: next
        })
        return
      }
      shareReuseStats.heldAgainstReplace += 1
    }
  })
}

function pin(
  args: BeforeLoadShareArgs,
  versions: Record<string, ShareEntry>,
  version: string
): BeforeLoadShareArgs {
  if (isInUse(versions[version])) shareReuseStats.reused += 1
  else shareReuseStats.highest += 1
  claimed.add(versions[version])
  holdInScope(versions, version)
  const { shareInfo } = args
  return {
    ...args,
    // A copy, never the remote's own config object: the exact version only
    // steers this one resolution. `singleton: false` makes a singleton
    // consumer resolve that exact version too; the runtime reads the flag only
    // to pick a version, and a pinned version is always a registered one.
    shareInfo: shareInfo && {
      ...shareInfo,
      shareConfig: {
        ...shareInfo.shareConfig,
        requiredVersion: version,
        singleton: false
      }
    }
  }
}

/**
 * `onSecondCopy` fires when a consumer has to start a copy while another
 * version of the package is already loaded or loading: some deployed remote
 * bundles a copy below another's range. The plugin cannot avoid that one
 * without breaking a range; the report says which app to refresh.
 */
export function shareReusePlugin({
  onSecondCopy
}: {
  onSecondCopy?: (copy: SecondCopy) => void
} = {}): ModuleFederationRuntimePlugin {
  return {
    name: 'share-reuse-plugin',
    async beforeLoadShare(args) {
      shareReuseStats.calls += 1
      try {
        let choice = choose(args)
        if (
          choice &&
          (choice.version === undefined ||
            !isInUse(choice.versions[choice.version]))
        ) {
          await waitForBootingRemotes()
          choice = choose(args)
        }
        if (choice) reportSecondCopy(args, choice, onSecondCopy)
        if (choice?.version !== undefined)
          return pin(args, choice.versions, choice.version)
      } catch {
        // A version preference must never stop a module from loading.
      }
      shareReuseStats.skipped += 1
      return args
    },
    // Every entry Module Federation hands out stays in scope, not only the ones
    // chosen above: singletons with no copy in use, and skipped consumers,
    // resolve without the plugin's pin. Production: hr shares @inno-ui/toast as a singleton, its
    // runtime started timetracking's 1.0.3 and renamed it `from: hr`, and
    // task_management (sorting after hr) replaced it mid-load. Lookups that
    // only probe the scope (registerShared, loadShareSync) hold too: that only
    // fixes which remote's chunk serves the version, never which version.
    // Nothing is claimed here: a real load sets `loading` on the entry in the
    // same tick, and a probe that loads nothing must not count as in use.
    resolveShare(params) {
      const { resolver, shareScopeMap, scope, pkgName } = params
      params.resolver = () => {
        const result = resolver()
        try {
          // 0.x returns the entry, 2.x returns { shared, useTreesShaking }.
          type Resolved = ShareEntry & { version?: string }
          const raw = result as unknown as Resolved | { shared: Resolved }
          const entry = raw && 'shared' in raw ? raw.shared : raw
          const versions = shareScopeMap?.[scope]?.[pkgName]
          if (entry?.version && versions) {
            holdInScope(versions, entry.version, entry)
            // The runtime sets `loading` synchronously right after resolving;
            // watch it before any later consumer can be steered to it.
            queueMicrotask(() => watchLoading(entry))
          }
        } catch {
          // Holding is an optimisation; the resolved entry is returned as is.
        }
        return result
      }
      return params
    },
    initContainer(args) {
      const name = args.remoteInfo?.name
      if (name) remoteBooted(name)
      return args
    }
  }
}
